import { createFileRoute, useNavigate, Link, useHydrated } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { primaryShell, useAuth } from "@/hooks/use-auth";
import { checkSsoRequirement } from "@/lib/sso-policy.functions";
import { AuthShell } from "@/components/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { logger } from "@/lib/logger";

function getBootstrapStatus({
  hydrated,
  loading,
  user,
  authError,
  submitting,
}: {
  hydrated: boolean;
  loading: boolean;
  user: unknown;
  authError: string | null;
  submitting: boolean;
}) {
  if (!hydrated) return "Preparing sign-in…";
  if (submitting) return "Signing in…";
  if (loading && user) return "Finalizing your workspace access…";
  if (authError) return "Sign-in is available, but access setup reported an error.";
  return null;
}

function getAuthDebugContext() {
  if (typeof window === "undefined") {
    return {
      currentOrigin: null,
      redirectTarget: null,
      callbackPath: null,
    };
  }

  const url = new URL(window.location.href);

  return {
    currentOrigin: url.origin,
      redirectTarget: url.searchParams.get("redirect") ?? "/scoreboard",
    callbackPath: `${url.pathname}${url.search}${url.hash}`,
  };
}

export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({
    meta: [
      { title: "Sign in — WMS" },
      { name: "description", content: "Sign in to your warehouse management workspace." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Sign in — WMS" },
      { property: "og:description", content: "Sign in to your warehouse management workspace." },
    ],
  }),
});

function LoginPage() {
  const hydrated = useHydrated();
  const { user, loading, authError, roles } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    logger.info("Login page loaded", getAuthDebugContext());
  }, []);

  useEffect(() => {
    logger.info("Login auth bootstrap state", {
      ...getAuthDebugContext(),
      loading,
      hasUser: Boolean(user),
      hasRoles: roles.length > 0,
      authErrorMessage: authError,
    });
  }, [loading, user, roles.length, authError]);

  useEffect(() => {
    if (loading || !user) return;
    const shell = primaryShell(roles);
    if (shell === "app") {
      logger.info("Resolved post-login destination", {
        ...getAuthDebugContext(),
        redirectTarget: "/scoreboard",
        sessionCreated: true,
      });
      navigate({ to: "/scoreboard", replace: true });
      return;
    }
    if (shell === "portal") {
      logger.info("Resolved post-login destination", {
        ...getAuthDebugContext(),
        redirectTarget: "/portal/workspace",
        sessionCreated: true,
      });
      navigate({ to: "/portal/workspace", replace: true });
      return;
    }
    logger.warn("Authenticated user has no mapped shell", {
      ...getAuthDebugContext(),
      redirectTarget: "/no-access",
      sessionCreated: true,
    });
    navigate({ to: "/no-access", replace: true });
  }, [loading, user, roles, navigate]);

  const onSubmit = async () => {
    if (!hydrated) {
      logger.warn("Password sign-in attempted before hydration", getAuthDebugContext());
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const debugContext = getAuthDebugContext();
    setLoginError(null);
    setSubmitting(true);
    logger.info("Password sign-in started", debugContext);
    try {
      const { requireSso } = await checkSsoRequirement({ data: { email: normalizedEmail } });
      if (requireSso) {
        setSubmitting(false);
        logger.warn("Password sign-in blocked by SSO policy", {
          ...debugContext,
          sessionCreated: false,
          authErrorMessage: "SSO required for email domain",
        });
        toast.error("This domain requires SSO. Contact your admin for the IdP-initiated sign-in URL.");
        return;
      }
    } catch {
      // fail-open: if the policy lookup errors, continue with password auth
    }

    let data: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>["data"] | null = null;
    let error: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>["error"] | null = null;

    try {
      const result = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
      data = result.data;
      error = result.error;
    } catch (err) {
      setSubmitting(false);
      const message = err instanceof Error ? err.message : "Unable to sign in";
      logger.error("Password sign-in threw", err, {
        ...debugContext,
        authErrorMessage: message,
        sessionCreated: false,
      });
      setLoginError(message);
      toast.error(message);
      return;
    }

    setSubmitting(false);
    if (error) {
      logger.error("Password sign-in failed", error, {
        ...debugContext,
        authErrorMessage: error.message || "Unable to sign in",
        sessionCreated: Boolean(data?.session),
      });
      const message = error.message || "Unable to sign in";
      setLoginError(message);
      toast.error(message);
      return;
    }

    logger.info("Password sign-in succeeded", {
      ...debugContext,
      sessionCreated: Boolean(data?.session),
      hasUser: Boolean(data?.user),
    });
  };

  const onGoogle = async () => {
    const { lovable } = await import("@/integrations/lovable/index");
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/`,
    });
    if (result.error) toast.error(result.error.message ?? "Sign-in failed");
  };

  const bootstrapStatus = getBootstrapStatus({ hydrated, loading, user, authError, submitting });

  return (
    <AuthShell title="Sign In" subtitle="Welcome back to your WMS">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        {bootstrapStatus && !(loginError || authError) && (
          <Alert>
            <AlertDescription>{bootstrapStatus}</AlertDescription>
          </Alert>
        )}
        {(loginError || authError) && (
          <Alert variant="destructive">
            <AlertDescription>
              {loginError ?? authError}
              {import.meta.env.DEV && authError ? ` (auth-init)` : ""}
            </AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" disabled={submitting} />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:underline">
              Forgot?
            </Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" disabled={submitting} />
        </div>
        <Button type="submit" className="w-full" disabled={submitting || !hydrated}>
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Sign in
        </Button>
        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">or</span>
          </div>
        </div>
        <Button type="button" variant="outline" className="w-full" onClick={onGoogle} disabled={submitting}>
          Continue with Google
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          No account? <Link to="/signup" className="text-primary hover:underline">Sign up</Link>
        </p>
      </form>
    </AuthShell>
  );
}